import { type ReactElement, type ReactNode, useState } from 'react'

import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'

import { BrandLogo } from './brand-logo'
import { Sidebar, SidebarNav } from './sidebar'
import { Topbar } from './topbar'

export interface AppShellProps {
  readonly children: ReactNode
}

/**
 * Casca do painel: sidebar + topbar + área de conteúdo.
 *
 * Comportamento por largura (os três pontos exigidos pela DoD):
 *   - 375px  → sidebar vira gaveta, aberta pelo botão de menu da topbar.
 *   - 768px  → trilho de ícones de 4rem, sempre visível.
 *   - 1440px → sidebar de 16rem com rótulos.
 *
 * Só a área de conteúdo rola: a topbar fica grudada no topo e a sidebar não
 * acompanha a rolagem.
 */
export function AppShell({ children }: AppShellProps): ReactElement {
  const [gavetaAberta, setGavetaAberta] = useState(false)

  return (
    // O fio de 2px de `--brand-500` no topo é a assinatura amarela do shell
    // ("o fio da blindagem"): `border-primary` (camada utilities) vence o
    // `border-color: var(--color-border)` global do @layer base do tema.
    <div className="flex h-full min-h-0 border-t-2 border-primary bg-background">
      <Sidebar />

      <Dialog open={gavetaAberta} onOpenChange={setGavetaAberta}>
        <DialogContent
          variant="drawer"
          className="bg-sidebar p-0"
          hideCloseButton
          // O título já descreve a gaveta; sem isto o Radix reclama da falta
          // de descrição no console.
          aria-describedby={undefined}
        >
          <div className="flex h-14 items-center border-b border-border px-4">
            <BrandLogo />
          </div>
          <DialogTitle className="sr-only">Navegação principal</DialogTitle>
          <div className="flex-1 overflow-y-auto">
            <SidebarNav
              aoNavegar={() => {
                setGavetaAberta(false)
              }}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* A textura fica aqui — e não na raiz nem no <main> — para a sidebar
          opaca ficar limpa e para quem rola ser o <main> filho: o fundo fica
          estático sob o conteúdo, sem shimmer e sem background-attachment. */}
      <div className="flex min-w-0 flex-1 flex-col bg-blindagem">
        <Topbar
          aoAbrirNavegacao={() => {
            setGavetaAberta(true)
          }}
        />
        <main className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">{children}</main>
      </div>
    </div>
  )
}
